import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  ComposedChart,
  Area,
  BarChart,
  Bar,
  Sector,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import {
  Cloud,
  Sun,
  CloudRain,
  CloudSnow,
  Wind,
  Thermometer,
  Droplets,
  Eye,
} from "lucide-react";
import {
  useSite,
  useFinance,
  useMaterials,
  useDeliveries,
  useSiteMembers,
  useTaskReport,
  useWorkerReport,
  useWeather,
  useGPSPreference,
  useMaterialRequests,
} from "../../../utils/hooks/useSiteData";
import { useAlert } from "components/UI_components/Alert";
import LoadingScreen from "../../../components/UI_components/LoadingScreen";
import SkeletonLoader from "../../../components/UI_components/SkeletonLoading";
import { Circles } from "react-loader-spinner";
import CommunicationsPanel from "./CommunicationPanel";
import LiveLocationTracker from "./LocationTracker";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import NoPermissionState from "components/UI_components/NoPermissionState";
import {
  MdOutlineNorthEast,
  MdOutlinePerson,
  MdOutlineSouthWest,
} from "react-icons/md";
import { BASE_URL } from "utils/constant";
import { getFinanceCategories } from "utils/getFinanceCategories";
import CheckIcon from "assets/check_24px.png";
import CrossIcon from "assets/close_24px.png";
import TruckIcon from "assets/local_shipping.png";
import WarningIcon from "assets/exclamation.png";
import { convertToStringIndex } from "screens/Dashboard/Contractors/ContractorDetails";
import { useNavigate } from "react-router-dom";

const MONTHS_ORDER = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Modern SaaS Palette
const THEME = {
  primary: "#3B0069",
  secondary: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
  },
};
const COLORS = [
  "#3B0069",
  "#7C3AED",
  "#EC4899",
  "#F59E0B",
  "#10B981",
  "#6366F1",
];

const getFormattedTransactionId = (transaction, categories) => {
  const { materialCategory, workerCategory } = categories;
  switch (transaction.transaction_catagory_id) {
    case materialCategory:
      return transaction?.po_number
        ? `${transaction.po_number}`
        : `DLVRY-${convertToStringIndex(transaction.id)}`;
    case workerCategory:
      return `WORKEY_PAY-${convertToStringIndex(transaction.id)}`;
    default:
      return `TRN-${convertToStringIndex(transaction.id)}`;
  }
};
const formatAmount = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const MaterialStats = ({ transactions }) => {
  const [showAll, setShowAll] = useState(false);

  // Group materials by type and calculate totals
  const materialStats = transactions?.reduce((acc, material) => {
    const key =
      material.material_name +
      "?" +
      material.subcategory +
      "?" +
      material.specification;
    if (!acc[key]) {
      acc[key] = { name: key, amount: 0, value: 0, uom: material.uom };
    }
    acc[key].amount += material.current_stock;
    acc[key].value += material.current_stock;

    return acc;
  }, {});

  // Build and sort chart data
  const totalAmount = transactions?.reduce(
    (sum, t) => sum + t.current_stock,
    0,
  );
  const chartData = Object.values(materialStats)
    .sort((a, b) => b.amount - a.amount)
    .map((stat) => ({
      ...stat,
      percent:
        totalAmount > 0 ? Math.round((stat?.amount / totalAmount) * 100) : 0,
    }));

  // Decide which slice of data to show
  const displayedData = showAll ? chartData : chartData.slice(0, 4);

  const COLORS = [
    "#3B0069",
    "#7C3AED",
    "#EC4899",
    "#F59E0B",
    "#10B981",
    "#6366F1",
  ];

  return (
    <div className="flex flex-col xl:flex-row items-center justify-between bg-white p-4 xl:p-6 rounded-xl shadow-sm border border-slate-100 mb-6 gap-6 xl:gap-8">
      <div className="w-48 h-48 xl:w-64 xl:h-64 relative flex-shrink-0">
        <div className="hidden xl:block">
          <PieChart width={256} height={256}>
            <Pie
              data={displayedData}
              cx={128}
              cy={128}
              startAngle={90}
              endAngle={-270}
              innerRadius={80}
              outerRadius={110}
              paddingAngle={4}
              dataKey="percent"
              cornerRadius={6}
            >
              {displayedData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={COLORS[index % COLORS.length]}
                  strokeWidth={0}
                />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="xl:hidden">
          <PieChart width={192} height={192}>
            <Pie
              data={displayedData}
              cx={96}
              cy={96}
              startAngle={90}
              endAngle={-270}
              innerRadius={60}
              outerRadius={85}
              paddingAngle={4}
              dataKey="percent"
              cornerRadius={6}
            >
              {displayedData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={COLORS[index % COLORS.length]}
                  strokeWidth={0}
                />
              ))}
            </Pie>
          </PieChart>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-[10px] xl:text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5 xl:mb-1">
            Total Stock
          </div>
          <div className="text-xl xl:text-2xl font-bold text-slate-800">
            {totalAmount?.toLocaleString()}
          </div>
          <div className="text-[10px] xl:text-xs text-slate-400 mt-0.5 xl:mt-1">units</div>
        </div>
      </div>

      <div className="flex-1 w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Material Breakdown
          </h3>
        </div>
        <div
          className="space-y-3 max-h-60 overflow-auto pr-2"
          style={{ scrollbarWidth: "thin" }}
        >
          {displayedData?.map((item, index) => (
            <div
              key={item.name}
              className="flex items-center justify-between group p-1.5 xl:p-2 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2 xl:gap-3 flex-1 min-w-0">
                <div
                  className="w-2.5 h-2.5 xl:w-3 xl:h-3 rounded-full ring-2 ring-white shadow-sm flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span
                  className="text-xs xl:text-sm font-medium text-slate-700 truncate"
                  title={item.name.split("?")[0]}
                >
                  {item.name.split("?")[0]}
                </span>
              </div>
              <div className="flex items-center gap-2 xl:gap-4 flex-shrink-0">
                <div className="flex flex-col items-end">
                  <span className="text-xs xl:text-sm font-bold text-slate-800">
                    {item.percent}%
                  </span>
                </div>
                <div className="flex flex-col items-end w-20 xl:w-24">
                  <span className="text-xs xl:text-sm font-medium text-slate-600">
                    {item.amount.toLocaleString()}
                  </span>
                  <span className="text-[9px] xl:text-[10px] text-slate-400 uppercase truncate max-w-full">
                    {item.uom}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {displayedData?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <div className="bg-slate-50 p-3 rounded-full mb-2">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <span className="text-sm">No material data available</span>
            </div>
          )}
        </div>

        {chartData?.length > 4 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-4 w-full py-2 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors border border-transparent hover:border-purple-100"
          >
            {showAll ? "Show Less" : `Show All (${chartData.length})`}
          </button>
        )}
      </div>
    </div>
  );
};

const FinanceStats = ({ transactions, categoryData }) => {
  const [pieData, setPieData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const {
    categoryNames,
    refundCategory,
    receivedCategory,
  } = categoryData;

  const calculatePieData = () => {
    let categoryTotals = {};
    Object.keys(categoryNames).forEach((key) => {
      categoryTotals[key] = 0;
    });

    transactions.forEach((transaction) => {
      const categoryId = transaction?.transaction_catagory_id;
      const amount = transaction?.amount ?? transaction?.paid_amount ?? 0;

      if (categoryId in categoryTotals) {
        categoryTotals[categoryId] += amount;
      }
    });

    const total = Object.values(categoryTotals)?.reduce(
      (sum, amount) => sum + amount,
      0,
    );
    // total amount without refund i.e category 7
    const totalWithoutRefund = Object.entries(categoryTotals).reduce(
      (sum, [key, amount]) => {
        const catId = parseInt(key, 10);
        if (catId === receivedCategory) {
          return sum;
        }
        if (catId === refundCategory) {
          return sum;
        }
        return sum + amount;
      },
      0,
    );
    setTotalAmount(totalWithoutRefund);
    const pieData = Object.entries(categoryTotals)
      .filter(([_, amount]) => amount > 0)
      .map(([categoryId, amount]) => ({
        name: categoryNames[categoryId],
        value: Math.round((amount / total) * 100),
        amount: amount,
      }))
      .sort((a, b) => b.value - a.value);

    setPieData(pieData);
  };

  useEffect(() => {
    if (Object.keys(categoryNames).length > 0) {
      calculatePieData();
    }
  }, [transactions, categoryNames]);

  return (
    <div className="flex flex-col xl:flex-row items-center justify-between bg-white p-4 xl:p-6 rounded-xl shadow-sm border border-slate-100 mb-6 gap-6 xl:gap-8">
      <div className="w-[280px] xl:w-[320px] h-[240px] xl:h-[280px] relative overflow-visible flex flex-col items-center flex-shrink-0">
        {/* Concentric half-circle chart */}
        {(() => {
          const isXL = typeof window !== 'undefined' && window.innerWidth >= 1280;
          const size = isXL ? 320 : 280;
          const centerX = size / 2;
          const centerY = size / 2 + (isXL ? 42 : 36); // adjusted center
          const ringWidth = isXL ? 16 : 14;
          const ringGap = isXL ? 12 : 10;
          const displayed = pieData.slice(0, 5);
          const baseRadius = size / 2 - ringWidth / 2 - 8;

          // Helper to build a top half-arc path for a given radius
          const arcPath = (r) => {
            const startX = centerX - r;
            const endX = centerX + r;
            const y = centerY;
            // Draw top semicircle from left to right
            return `M ${startX} ${y} A ${r} ${r} 0 0 1 ${endX} ${y}`;
          };

          return (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              {displayed.map((item, index) => {
                const radius = baseRadius - index * (ringWidth + ringGap);
                const totalLen = Math.PI * radius; // half circumference
                const progress = Math.max(0, Math.min(100, item.value || 0));
                const dash = (progress / 100) * totalLen;
                const color = COLORS[index % COLORS.length];
                return (
                  <g key={item.name}>
                    {/* Track (full half-arc) */}
                    <path
                      d={arcPath(radius)}
                      fill="none"
                      stroke="#F1F5F9"
                      strokeWidth={ringWidth}
                      strokeLinecap="round"
                    />
                    {/* Progress (portion of the half-arc) */}
                    <path
                      d={arcPath(radius)}
                      fill="none"
                      stroke={color}
                      strokeWidth={ringWidth}
                      strokeLinecap="round"
                      strokeDasharray={`${dash} ${Math.max(0, totalLen - dash)}`}
                    />
                  </g>
                );
              })}
            </svg>
          );
        })()}
        <div className="mt-[-30px] xl:mt-[-40px] text-center">
          <div className="text-[10px] xl:text-xs font-medium text-slate-500 uppercase tracking-wider mb-0.5 xl:mb-1">
            Total Expense
          </div>
          <div className="text-xl xl:text-2xl font-bold text-slate-800">
            {formatAmount(totalAmount)}
          </div>
        </div>
      </div>

      <div className="flex-1 w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Expense Categories
          </h3>
        </div>
        <div className="space-y-3">
          {pieData?.map((item, index) => (
            <div
              key={item.name}
              className="flex items-center justify-between group p-1.5 xl:p-2 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-2 xl:gap-3 flex-1 min-w-0">
                <div
                  className="w-2.5 h-2.5 xl:w-3 xl:h-3 rounded-full ring-2 ring-white shadow-sm flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-xs xl:text-sm font-medium text-slate-700 truncate">
                  {item.name}
                </span>
              </div>
              <div className="flex items-center gap-2 xl:gap-4 flex-shrink-0 ml-2">
                <span className="text-xs xl:text-sm font-bold text-slate-800 w-10 xl:w-12 text-right">
                  {item.value}%
                </span>
                <span className="text-xs xl:text-sm font-medium text-slate-600 w-20 xl:w-24 text-right">
                  {formatAmount(item.amount)}
                </span>
              </div>
            </div>
          ))}
          {pieData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <div className="bg-slate-50 p-3 rounded-full mb-2">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <span className="text-sm">No expense data available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const Home = ({
  setTab,
  userPermissions,
  setSnagId,
  triggerRefresh,
  permissionsLoading,
  isProfileCardOpen, // This should already be here
}) => {
  const API_KEY = "c093db40219c1637dbc236b56549afdd";
  const siteId =
    typeof window !== "undefined" ? localStorage.getItem("site") : null;

  const { data: site, isLoading: siteLoading } = useSite(siteId);
  const { data: finance, isLoading: financeLoading } = useFinance(siteId);
  const { data: materials, isLoading: materialsLoading } = useMaterials(siteId);
  const { data: materialRequests, isLoading: requestsLoading } = useMaterialRequests(siteId);
  const { data: deliveries, isLoading: deliveriesLoading } = useDeliveries(siteId);
  const { data: members, isLoading: membersLoading } = useSiteMembers(siteId);
  const { data: taskReport, isLoading: taskReportLoading } = useTaskReport(siteId);
  const { data: workerReport, isLoading: workerReportLoading } = useWorkerReport(siteId);
  const navigate = useNavigate();

  const total = taskReport?.Total ?? 0;
  const completedCount = taskReport?.Completed ?? 0;
  const ongoingCount = taskReport?.Ongoing ?? 0;
  const haltedCount = taskReport?.Halted ?? 0;
  const overdueCount = 0; // The new API doesn't provide overdue count, defaulting to 0 or we can calculate if needed

  const completedPct = total ? (completedCount / total) * 100 : 0;
  const ongoingPct = total ? (ongoingCount / total) * 100 : 0;
  const haltedPct = total ? (haltedCount / total) * 100 : 0;

  const taskPieData = [
    { name: "Completed", value: completedCount },
    { name: "Ongoing", value: ongoingCount },
    { name: "Halted", value: haltedCount },
  ];

  const TASK_COLORS = {
    Completed: "#4F46E5", // Indigo
    Ongoing: "#22C55E", // Green
    Halted: "#F59E0B", // Amber/Orange
  };
  const completedIndex = taskPieData.findIndex((d) => d.name === "Completed");
  const ongoingTasks = taskReport?.recent_tasks || [];
  // helpers to render task assignees
  const siteMemberById =
    Array.isArray(members) &&
    members.reduce((acc, m) => {
      const id = m?.member_id || m?.id;
      if (id) acc[id] = m;
      return acc;
    }, {});
  const getTaskAssignees = (task) => {
    // Prefer explicit allocated_members if includes names/pics
    if (
      Array.isArray(task?.allocated_members) &&
      task.allocated_members.length
    ) {
      return task.allocated_members;
    }
    // Else map task_members (ids) to site members
    if (Array.isArray(task?.task_members) && siteMemberById) {
      return task.task_members
        .map((tm) => siteMemberById[tm?.member_id])
        .filter(Boolean);
    }
    return [];
  };

  const city = site?.city;
  const { data: weather, isLoading: weatherLoading, isError: weatherError } = useWeather(city, API_KEY);
  const { data: gpsEnabled, isLoading: gpsLoading } = useGPSPreference(siteId, triggerRefresh);

  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [transactionCatagories, setTransactionCatagories] = useState([]);
  const [categoryData, setCategoryData] = useState({
    categoryNames: {},
    materialCategory: null,
    otherCategory: null,
    workerCategory: null,
    refundCategory: null,
    receivedCategory: null,
    data: [],
  });
  const [isCommsExpanded, setIsCommsExpanded] = useState(true);

  const MAX_ITEMS = 3;
  const { addAlert } = useAlert();
  const STATUS_COLORS = {
    Ongoing: {
      background: "bg-[#238B2826]",
      text: "text-[#238B28]",
      dot: "bg-[#238B28]",
    },
    Completed: {
      background: "bg-[#8E641226]",
      text: "text-[#8E6412]",
      dot: "bg-[#8E6412]",
    },
    Halted: {
      background: "bg-[#B7191926]",
      text: "text-[#B71919]",
      dot: "bg-[#B71919]",
    },
    Upcoming: {
      background: "bg-[#1A178D26]",
      text: "text-[#1A178D]",
      dot: "bg-[#1A178D]",
    },
  };

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        setCategoriesLoading(true);
        const res = await getFinanceCategories();
        setCategoryData(res);
        setTransactionCatagories(res.data || []);
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchTransactionCatagories();
  }, []);

  const formatDate = () => {
    const today = new Date();
    const options = { day: "numeric", month: "long", year: "numeric" };
    return today.toLocaleDateString("en-GB", options);
  };

  const formatTransactionTimeDate = (dateString) => {
    if (!dateString) return { time: "", date: "" };
    const date = new Date(dateString);
    const time = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return { time, date: `${day} ${month}, ${year}` };
  };

  // Mini KPI card with sparkline (inspired by Finance.js MetricCard)
  const MiniKpiCard = ({
    title,
    value,
    data = [],
    color = "#22C55E",
    periodLabel = "Last 30 days",
  }) => {
    const width = 120;
    const height = 36;
    const series = data?.length ? data : [3, 4, 5, 6, 7, 6, 7, 8];
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = Math.max(1, max - min);
    const stepX = width / (series.length - 1);
    const points = series
      .map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * height;
        return `${x},${y}`;
      })
      .join(" ");
    const firstY = height - ((series[0] - min) / range) * height;
    const lastX = (series.length - 1) * stepX;
    const linePath = `M0,${firstY} L ${points}`;
    const areaPath = `${linePath} L ${lastX},${height} L 0,${height} Z`;
    const gradientId = React.useMemo(
      () =>
        `miniKpiGradient-${String(title || "kpi")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")}`,
      [title],
    );

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 w-[160px]">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-medium text-gray-600">{title}</span>
          <span className="inline-flex items-center gap-1 text-[9px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-[1px] rounded-md">
            {periodLabel}
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 10l5 5 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <div className="text-base font-extrabold text-gray-900 mb-1">
          {value}
        </div>
        <div className="h-[36px]">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.30" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradientId})`} />
            <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" />
          </svg>
        </div>
      </div>
    );
  };

  // Popup state for Workers & Attendance chart
  const [attendancePopup, setAttendancePopup] = useState(null);
  const attendanceChartRef = React.useRef(null);

  // Compact metric card for Workers section (value, hardcoded trend, hardcoded bars)
  const WorkersMetricCard = ({
    label,
    value,
    color = "#22C55E",
    trendText = "+3.07%",
    direction = "up",
    bars = [8, 12, 16, 22, 18],
    valueClassName = "text-[#1E293B]",
  }) => {
    const arrow =
      direction === "down" ? (
        <MdOutlineSouthWest size={12} />
      ) : (
        <MdOutlineNorthEast size={12} />
      );
    return (
      <div className="flex flex-col w-full rounded-xl bg-white shadow-sm border border-gray-200 p-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <div
              className={`text-2xl font-bold leading-tight ${valueClassName}`}
            >
              {value}
            </div>
            <div
              className={`mt-1 inline-flex items-center gap-1 text-xs font-medium`}
              style={{
                color: direction === "down" ? "#EC4899" : "#16A34A",
              }}
            >
              {arrow}
              {trendText}
            </div>
            <div className="mt-2 text-sm text-[#475569] font-medium">
              {label}
            </div>
          </div>

          <div className="flex items-end gap-1 h-10">
            {bars.map((h, i) => (
              <div
                key={i}
                className="w-[6px] rounded-t"
                style={{
                  height: `${h}px`,
                  backgroundColor: color,
                  opacity: 0.5 + i / (bars.length * 2),
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Close the attendance popup when clicking outside the chart area
  useEffect(() => {
    const handleClickAway = (e) => {
      if (!attendancePopup) return;
      const container = attendanceChartRef.current;
      if (container && !container.contains(e.target)) {
        setAttendancePopup(null);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [attendancePopup]);

  const handleAttendanceChartClick = (state) => {
    if (!state || !state.activePayload || !state.activeCoordinate) {
      setAttendancePopup(null);
      return;
    }
    const label = state.activeLabel;
    const present = state.activePayload.find(
      (p) => p.dataKey === "Present",
    )?.value;
    const absent = state.activePayload.find(
      (p) => p.dataKey === "Absent",
    )?.value;
    const x = state.activeCoordinate.x;
    const y = state.activeCoordinate.y;

    const container = attendanceChartRef.current;
    const containerWidth = container?.clientWidth || 0;
    const containerHeight = container?.clientHeight || 0;
    // Keep the popup small and to the right of the bar top
    const CARD_WIDTH = 160;
    const CARD_HEIGHT = 96; // includes the small "Absent" pill
    const gutter = 8;
    const left = Math.min(
      containerWidth - CARD_WIDTH - gutter,
      Math.max(gutter, x + 12),
    );
    const top = Math.min(
      containerHeight - CARD_HEIGHT - gutter,
      Math.max(gutter, y - CARD_HEIGHT / 2),
    );
    const presentSeries = workerReport?.recent_attendance
      ? Object.values(workerReport.recent_attendance)
        .map((list) => list.filter((a) => a.attendance === "Present").length)
        .reverse()
      : [];

    setAttendancePopup({
      label,
      present,
      absent,
      left,
      top,
      data: presentSeries,
    });
  };

  // Build monthly series for Finance chart: Received (Goal) vs Expenses (Complete)
  const financeChartData = React.useMemo(() => {
    const { receivedCategory, refundCategory } = categoryData;
    const rows = finance?.list || [];
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const now = new Date();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      const key = `${d.toLocaleString("default", {
        month: "short",
      })} ${d.getFullYear()}`;
      const label = d.toLocaleString("default", { month: "short" });
      return { key, label };
    });
    const grouped = {};
    for (const t of rows) {
      const dt = new Date(t.transaction_date || t.date);
      const key = `${dt.toLocaleString("default", {
        month: "short",
      })} ${dt.getFullYear()}`;
      const amount =
        Number(t?.amount ?? t?.paid_amount ?? t?.total_price ?? 0) || 0;
      if (!grouped[key]) grouped[key] = { received: 0, expense: 0, refund: 0 };
      if (String(t?.transaction_catagory_id) === String(receivedCategory)) {
        grouped[key].received += amount;
      } else if (
        String(t?.transaction_catagory_id) === String(refundCategory)
      ) {
        grouped[key].refund += amount;
      } else {
        grouped[key].expense += amount;
      }
    }
    return months.map(({ key, label }) => {
      const g = grouped[key] || { received: 0, expense: 0, refund: 0 };
      const expenseNet = Math.max(0, g.expense - g.refund);
      return {
        name: label,
        Expenses: Math.round(expenseNet), // purple filled area
        Received: Math.round(g.received), // orange line
        Refunds: Math.round(g.refund), // orange line
      };
    });
  }, [finance?.list, categoryData]);

  // Custom tooltip for Finance chart
  const FinanceTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload;
    // console.log(data);
    return (
      <div className="bg-white/90 backdrop-blur rounded-lg shadow-md border border-gray-100 p-3">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: "#F59E0B" }}
            />
            <div className="text-xs text-gray-600">Expenses</div>
          </div>
          <div className="text-sm font-semibold text-gray-900">
            ₹{Number(data.Expenses).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: "#10B981" }}
            />
            <div className="text-xs text-gray-600">Received</div>
          </div>
          <div className="text-sm font-semibold text-gray-900">
            ₹{Number(data.Received).toLocaleString("en-IN")}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: "#7C3AED" }}
            />
            <div className="text-xs text-gray-600">Refunds</div>
          </div>
          <div className="text-sm font-semibold text-gray-900">
            ₹{Number(data.Refunds).toLocaleString("en-IN")}
          </div>
        </div>
      </div>
    );
  };

  const globalLoading =
    siteLoading ||
    financeLoading ||
    materialsLoading ||
    requestsLoading ||
    deliveriesLoading ||
    membersLoading ||
    taskReportLoading ||
    workerReportLoading ||
    categoriesLoading ||
    permissionsLoading;

  return (
    <LoadingScreen loading={globalLoading}>
      <div className="flex h-full justify-between bg-[#F8FAFC]">
        <div
          className="flex-1 py-6 px-8 h-[91vh] bg-transparent"
          style={{
            overflowY: "scroll",
            scrollbarWidth: "none",
          }}
        >
          <div className="flex justify-start flex-col gap-6 w-full mb-8">
            {/* Date Indicator */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-col gap-1 min-w-0">
                <h1 className="text-xl xl:text-2xl font-bold text-slate-900 truncate">
                  {site?.site_name || "Site Loading ..."}
                </h1>
                <div className="flex items-center gap-2 text-sm text-slate-500 min-w-0">
                  {site && (
                    <div
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] xl:text-xs font-medium flex-shrink-0 ${STATUS_COLORS[site?.constructionStatus]?.background} ${STATUS_COLORS[site?.constructionStatus]?.text}`}
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${STATUS_COLORS[site?.constructionStatus]?.dot} animate-pulse`}
                      ></div>
                      {site?.constructionStatus} Work
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 xl:gap-4 flex-wrap">
                <div className="text-xs xl:text-sm font-medium text-slate-500 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm whitespace-nowrap">
                  Today: {formatDate()}
                </div>

                {/* Enhanced Weather Widget */}
                {weather && (
                  <div className="flex items-center px-3 xl:px-4 py-1 xl:py-1.5 bg-white rounded-xl shadow-sm border border-slate-200 transition-all hover:shadow-md">
                    {weatherLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
                        <span className="text-xs text-slate-500">Loading...</span>
                      </div>
                    ) : weatherError ? (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Cloud size={16} />
                        <span className="text-xs">Unavailable</span>
                      </div>
                    ) : weather ? (
                      <div className="flex items-center gap-2 xl:gap-4">
                        <div className="flex items-center">
                          <img
                            src={`https://openweathermap.org/img/wn/${weather?.weather?.[0]?.icon}@2x.png`}
                            alt={weather?.weather?.[0]?.description}
                            width={32}
                            className="-my-2"
                          />
                          <span className="text-base xl:text-lg font-bold text-slate-800 ml-0.5">
                            {Math.round(weather.main?.temp)}°
                          </span>
                        </div>
                        <div className="hidden sm:flex flex-col text-[10px] xl:text-xs text-slate-500 min-w-0">
                          <span className="font-medium text-slate-700 capitalize truncate">
                            {weather?.weather?.[0]?.description}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex items-center gap-0.5 whitespace-nowrap">
                              <Droplets size={10} />
                              <span>{weather.main?.humidity}%</span>
                            </div>
                            {weather.wind && (
                              <div className="flex items-center gap-0.5 whitespace-nowrap">
                                <Wind size={10} />
                                <span>{Math.round(weather.wind.speed)}m/s</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
          {(userPermissions?.view_finance ||
            userPermissions?.is_admin ||
            userPermissions?.view_all) && (
              <div className="flex flex-col gap-2 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 w-full">
                  {/* Estimated Budget */}
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm hover:shadow-md transition-shadow h-28">
                    <div className="flex flex-col justify-center h-full min-w-0">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5 truncate">
                        Estimated Budget
                      </span>
                      <span className="text-lg xl:text-xl font-bold text-slate-900 leading-tight truncate">
                        ₹ {site?.esrimated_cost?.toLocaleString() || 0}
                      </span>
                      <div className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md w-fit mt-1.5">
                        <MdOutlineNorthEast size={10} />
                        <span>On Track</span>
                      </div>
                    </div>
                  </div>

                  {/* Project Dues */}
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm hover:shadow-md transition-shadow h-28">
                    <div className="flex flex-col justify-center h-full min-w-0">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5 truncate">
                        Project Dues
                      </span>
                      <span className="text-lg xl:text-xl font-bold text-slate-900 leading-tight truncate">
                        ₹ {finance?.totals?.totalDue?.toLocaleString() || 0}
                      </span>
                      <div className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md w-fit mt-1.5">
                        <MdOutlineNorthEast size={10} />
                        <span>Pending</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Received */}
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm hover:shadow-md transition-shadow h-28">
                    <div className="flex flex-col justify-center h-full min-w-0">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5 truncate">
                        Total Received
                      </span>
                      <span className="text-lg xl:text-xl font-bold text-slate-900 leading-tight truncate">
                        ₹ {finance?.totals?.totalReceived?.toLocaleString() || 0}
                      </span>
                      <div className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md w-fit mt-1.5">
                        <MdOutlineNorthEast size={10} />
                        <span>+12%</span>
                      </div>
                    </div>
                  </div>

                  {/* Total Expenses */}
                  <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm hover:shadow-md transition-shadow h-28">
                    <div className="flex flex-col justify-center h-full min-w-0">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5 truncate">
                        Total Expenses
                      </span>
                      <span className="text-lg xl:text-xl font-bold text-slate-900 leading-tight truncate">
                        ₹ {finance?.totals?.totalExpense?.toLocaleString() || 0}
                      </span>
                      <div className="flex items-center gap-1 text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-md w-fit mt-1.5">
                        <MdOutlineNorthEast size={10} />
                        <span>High Spend</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          <div className="flex flex-col gap-4 bg-white py-6 px-6 mb-8 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-slate-800">
                Project Tasks Status
              </h2>
              <button
                onClick={() => navigate(`/site/${siteId}/tasks`)}
                className="text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-3 py-1 rounded-lg transition-colors"
              >
                View All
              </button>
            </div>
            <div className="flex flex-col xl:flex-row gap-8">
                  {/* Task List */}
                  <div className="flex-1 xl:max-w-[60%]">
                    <div className="flex items-center justify-between mb-4 px-2">
                      <span className="text-xs xl:text-sm font-semibold text-slate-500 uppercase tracking-wide">
                        In Progress
                      </span>
                      <span className="text-xs xl:text-sm font-semibold text-slate-500 uppercase tracking-wide">
                        Assignees
                      </span>
                    </div>
                    <div className="space-y-3">
                      {ongoingTasks
                        .slice(0, 4)
                        .map((task) => {
                          const assignees = task.assignees || [];
                          return (
                            <div
                              key={task.id}
                              className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors group border border-transparent hover:border-slate-100"
                            >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                                    />
                                  </svg>
                                </div>
                                <div className="flex flex-col max-w-[70%]">
                                  <span className="text-sm font-semibold text-slate-700 truncate group-hover:text-indigo-600 transition-colors">
                                    {task?.task_name}
                                  </span>
                                  <span className="text-xs text-slate-500 flex items-center gap-1">
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                    Due: {task?.end_date}
                                  </span>
                                </div>
                              </div>

                              <div className="flex -space-x-2 pl-4">
                                {assignees.slice(0, 4).map((m, idx) => {
                                  const name =
                                    [m?.first_name, m?.last_name]
                                      .filter(Boolean)
                                      .join(" ") || "User";
                                  const initials = name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase();
                                  const pic = m?.userpic || m?.profile_pic;
                                  return (
                                    <div
                                      key={idx}
                                      className="w-8 h-8 rounded-full ring-2 ring-white bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 overflow-hidden"
                                      title={name}
                                    >
                                      {pic ? (
                                        <img
                                          src={pic}
                                          alt={name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        initials
                                      )}
                                    </div>
                                  );
                                })}
                                {assignees.length > 4 && (
                                  <div className="w-8 h-8 rounded-full ring-2 ring-white bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                                    +{assignees.length - 4}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {ongoingTasks.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No ongoing tasks found
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Donut Chart */}
                  <div className="w-full xl:w-80 flex flex-col items-center justify-center border-t xl:border-t-0 xl:border-l border-slate-100 pt-6 xl:pt-0 xl:pl-8">
                    <div className="relative mb-6">
                      <PieChart width={160} height={160} className="xl:w-[200px] xl:h-[200px]">
                        <Pie
                          data={taskPieData}
                          cx="50%"
                          cy="50%"
                          startAngle={90}
                          endAngle={-270}
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={4}
                          cornerRadius={6}
                          dataKey="value"
                        >
                          {taskPieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={TASK_COLORS[entry.name]}
                              strokeWidth={0}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <div className="text-2xl xl:text-3xl font-bold text-indigo-600">
                          {Math.round(completedPct)}%
                        </div>
                        <div className="text-[10px] xl:text-xs font-medium text-slate-400 uppercase tracking-wider mt-1">
                          Completed
                        </div>
                      </div>
                    </div>

                    <div className="w-full space-y-2 xl:space-y-3">
                      {[
                        {
                          label: "Completed",
                          count: completedCount,
                          pct: completedPct,
                          color: TASK_COLORS.Completed,
                        },
                        {
                          label: "Ongoing",
                          count: ongoingCount,
                          pct: ongoingPct,
                          color: TASK_COLORS.Ongoing,
                        },
                        {
                          label: "Halted",
                          count: haltedCount,
                          pct: haltedPct,
                          color: TASK_COLORS.Halted,
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="flex items-center justify-between text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: item.color }}
                            ></span>
                            <span className="text-slate-600">{item.label}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-slate-900">
                              {item.count}
                            </span>
                            <span className="text-slate-400 w-12 text-right">
                              {item.pct.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
            </div>
          </div>
          {(userPermissions?.view_material_management ||
            userPermissions?.is_admin ||
            userPermissions?.view_all) && (
              <div className="flex flex-col gap-4 bg-white py-6 px-6 mb-8 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-slate-800">
                    Materials & Inventory
                  </h2>
                </div>
                {materials && <MaterialStats transactions={materials} />}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xl:gap-6">
                  {/* Material Requests */}
                  <div className="flex flex-col justify-between p-3 xl:p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all cursor-pointer group h-28 xl:h-32">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        Requests
                      </span>
                      <div className="p-1.5 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex items-end justify-between mt-1 xl:mt-2">
                      <span className="text-2xl xl:text-3xl font-bold text-slate-900 leading-none">
                        {materialRequests?.length || 0}
                      </span>
                      <div className="flex flex-col gap-1 text-[10px] font-medium text-slate-500">
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          {
                            materialRequests?.filter(
                              (mr) =>
                                mr.status !== "PENDING_APPROVAL" &&
                                mr.status !== "REJECTED",
                            ).length
                          }{" "}
                          Appr
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          {
                            materialRequests?.filter(
                              (mr) => mr.status === "PENDING_APPROVAL",
                            ).length
                          }{" "}
                          Pend
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Deliveries */}
                  <div className="flex flex-col justify-between p-3 xl:p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all cursor-pointer group h-28 xl:h-32">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] xl:text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        Deliveries
                      </span>
                      <div className="p-1.5 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                        <img
                          src={TruckIcon}
                          alt="Truck"
                          className="w-4 h-4 opacity-80"
                        />
                      </div>
                    </div>
                    <div className="flex items-end justify-between mt-1 xl:mt-2">
                      <span className="text-2xl xl:text-3xl font-bold text-slate-900 leading-none">
                        {
                          deliveries?.filter((item) => item.transaction_id !== 3)
                            .length
                        }
                      </span>
                      <div className="flex flex-col gap-1 text-[10px] font-medium text-slate-500">
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          {
                            deliveries?.filter((item) => item.transaction_id === 2)
                              .length
                          }{" "}
                          Dlvd
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                          {
                            deliveries?.filter((item) => item.transaction_id === 1)
                              .length
                          }{" "}
                          Schd
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inventory Alerts */}
                  <div className="flex flex-col justify-between p-3 xl:p-4 rounded-xl border border-rose-100 bg-rose-50/30 hover:shadow-md transition-all cursor-pointer group h-28 xl:h-32">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] xl:text-xs font-semibold text-rose-600 uppercase tracking-wide">
                        Alerts
                      </span>
                      <div className="p-1.5 bg-rose-100 rounded-lg group-hover:bg-rose-200 transition-colors">
                        <img src={WarningIcon} alt="Warning" className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="flex items-end justify-between mt-1 xl:mt-2">
                      <span className="text-2xl xl:text-3xl font-bold text-rose-600 leading-none">
                        {
                          materials?.filter((item) => item.current_stock <= 5)
                            .length
                        }
                      </span>
                      <div className="text-[10px] text-rose-700 bg-rose-100/50 px-2 py-1 rounded-md max-w-[60%] truncate">
                        {materials?.filter((item) => item.current_stock <= 5)
                          .length > 0 ? (
                          <span>
                            Low:{" "}
                            {
                              materials?.filter((item) => item.current_stock <= 5)
                                .length
                            }
                          </span>
                        ) : (
                          <span>Healthy</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {(userPermissions?.view_finance ||
            userPermissions?.is_admin ||
            userPermissions?.view_all) && (
              <div className="flex flex-col gap-4 bg-white py-6 px-6 mt-8 rounded-xl shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-slate-800">
                    Finance Overview
                  </h2>
                  <div className="flex items-center gap-2">
                    <select className="text-sm border border-slate-200 rounded-lg px-2 py-1 text-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500/20">
                      <option>This Year</option>
                      <option>Last Year</option>
                    </select>
                  </div>
                </div>

                <div className="w-full mt-2 rounded-xl bg-slate-50/50 p-2 xl:p-4 border border-slate-100">
                  <div className="h-64 xl:h-72 w-full">
                    <ResponsiveContainer height="100%" width="100%">
                      <ComposedChart
                        data={financeChartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id="colorOrangeFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#F59E0B"
                              stopOpacity={0.2}
                            />
                            <stop
                              offset="100%"
                              stopColor="#F59E0B"
                              stopOpacity={0}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorGreenFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#10B981"
                              stopOpacity={0.2}
                            />
                            <stop
                              offset="100%"
                              stopColor="#10B981"
                              stopOpacity={0}
                            />
                          </linearGradient>
                          <linearGradient
                            id="colorPurpleFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#7C3AED"
                              stopOpacity={0.2}
                            />
                            <stop
                              offset="100%"
                              stopColor="#7C3AED"
                              stopOpacity={0}
                            />
                          </linearGradient>

                        </defs>
                        <CartesianGrid
                          vertical={false}
                          stroke="#E2E8F0"
                          strokeDasharray="3 3"
                        />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#64748B", fontSize: 12 }}
                          dy={10}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#64748B", fontSize: 12 }}
                          width={60}
                          tickFormatter={(value) => `₹${value / 1000}k`}
                        />
                        <Tooltip
                          content={<FinanceTooltip />}
                          cursor={{ fill: "#7C3AED" }}
                        />
                        <Area
                          type="monotone"
                          dataKey="Expenses"
                          stroke="#F59E0B"
                          strokeWidth={2}
                          fill="url(#colorOrangeFill)"
                          name="Expenses"
                        />
                        <Area
                          type="monotone"
                          dataKey="Received"
                          stroke="#10B981"
                          strokeWidth={2}
                          fill="url(#colorGreenFill)"
                          name="Received"
                        />
                        <Area
                          type="monotone"
                          dataKey="Refunds"
                          stroke="#7C3AED"
                          strokeWidth={2}
                          fill="url(#colorPurpleFill)"
                          name="Refunds"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4">
                    <FinanceStats transactions={finance?.list || []} categoryData={categoryData} />
                  </div>
                </div>

                <div className="flex flex-col mt-4">
                  <h3 className="text-base font-semibold text-slate-800 mb-4">Recent Transactions</h3>
                  <div className="flex flex-col gap-2">
                    {finance?.list?.slice(0, 5).map((transaction) => {
                      const {
                        refundCategory,
                        receivedCategory,
                        materialCategory,
                        workerCategory,
                      } = categoryData;
                      return (
                        <div
                          key={transaction?.id}
                          className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100 group"
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${transaction?.transaction_catagory_id ==
                                refundCategory ||
                                transaction?.transaction_catagory_id ==
                                receivedCategory
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-rose-50 text-rose-600"
                                }`}
                            >
                              {transaction?.transaction_catagory_id ==
                                refundCategory ||
                                transaction?.transaction_catagory_id ==
                                receivedCategory ? (
                                <MdOutlineNorthEast size={18} />
                              ) : (
                                <MdOutlineSouthWest size={18} />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-700">
                                {transaction?.transaction_type ||
                                  (transaction?.transaction_catagory_id ===
                                    materialCategory
                                    ? "Material Purchase"
                                    : transaction?.transaction_catagory_id ===
                                      workerCategory
                                      ? "Worker Transaction"
                                      : transaction?.transaction_catagory_id ===
                                        refundCategory
                                        ? "Refund"
                                        : transaction?.transaction_catagory_id ===
                                          receivedCategory
                                          ? "Received"
                                          : "Others")}
                              </span>
                              <span className="text-xs text-slate-500">
                                #{getFormattedTransactionId(transaction, categoryData)} •{" "}
                                {transaction?.payment_status || "Paid"}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-6 w-[30%]">
                            <div className="flex flex-col items-start min-w-[110px]">
                              <span className="text-xs font-medium text-slate-700 text-left">
                                {transaction?.created_by || "Unknown"}
                              </span>
                              <div className="flex flex-col items-start text-[10px] text-slate-500 leading-tight">
                                <span>
                                  {
                                    formatTransactionTimeDate(transaction?.created_on)
                                      .time
                                  }
                                </span>
                                <span>
                                  {
                                    formatTransactionTimeDate(transaction?.created_on)
                                      .date
                                  }
                                </span>
                              </div>
                            </div>
                            <div
                              className={`flex items-center justify-end gap-0.5 text-sm font-bold leading-none ${transaction?.transaction_catagory_id ==
                                refundCategory ||
                                transaction?.transaction_catagory_id ==
                                receivedCategory
                                ? "text-emerald-600"
                                : "text-rose-600"
                                }`}
                            >
                              <span className="relative -top-[1px] text-sm font-bold">
                                {transaction?.transaction_catagory_id ==
                                  refundCategory ||
                                  transaction?.transaction_catagory_id == receivedCategory
                                  ? "+"
                                  : "-"}
                              </span>
                              <span className="tabular-nums">
                                ₹{Number(
                                  transaction?.amount ??
                                  transaction?.paid_amount ??
                                  transaction?.total_price ??
                                  0,
                                ).toLocaleString("en-IN")}
                              </span>
                            </div>
                            <button className="text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-full transition-colors opacity-0 group-hover:opacity-100">
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {finance?.list?.length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        No recent finance records
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          {(userPermissions?.view_worker_management ||
            userPermissions?.is_admin ||
            userPermissions?.view_all) && (
              <div className="flex flex-col gap-4 bg-white py-6 px-6 mt-8 rounded-xl shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-slate-800">
                    Workers & Attendance
                  </h2>
                  <button
                    onClick={() => navigate(`/site/${siteId}/workers`)}
                    className="text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-3 py-1 rounded-lg transition-colors"
                  >
                    Manage Workers
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <WorkersMetricCard
                    label="Total Workers"
                    value={workerReport?.total_workers || 0}
                    color="#10B981"
                    trendText="+3.07%"
                    direction="up"
                    bars={[6, 10, 14, 18, 16]}
                    valueClassName="text-slate-900"
                  />
                  <WorkersMetricCard
                    label="Absenteeism Rate"
                    value={(() => {
                      const todayDate = new Date().toISOString().split("T")[0];
                      const todayAttendance = workerReport?.recent_attendance?.[todayDate] || [];
                      if (todayAttendance.length === 0) return "0%";
                      const absentCount = todayAttendance.filter(a => a.attendance === "Absent").length;
                      return `${((absentCount / todayAttendance.length) * 100).toFixed(1)}%`;
                    })()}
                    color="#3B82F6"
                    trendText="+1.2%"
                    direction="up"
                    bars={[4, 7, 10, 14, 16]}
                    valueClassName="text-slate-900"
                  />
                  <WorkersMetricCard
                    label="Payment Due"
                    value={`₹${(Math.max(workerReport?.worker_payments?.due, 0) || 0).toLocaleString("en-IN")}`}
                    color="#EF4444"
                    // trendText="-5.4%"
                    direction="down"
                    bars={[14, 12, 10, 8, 6]}
                    valueClassName="text-rose-600"
                  />
                </div>

                <div className="flex flex-col xl:flex-row gap-8">
                  {/* Attendance Chart */}
                  <div
                    className="flex-1 xl:w-1/2 rounded-xl bg-slate-50/50 py-4 border border-slate-100 "
                    ref={attendanceChartRef}
                  >
                    <h3 className="text-xs xl:text-sm px-4 font-semibold text-slate-500 uppercase tracking-wide mb-4">
                      Weekly Attendance
                    </h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={(() => {
                          if (!workerReport?.recent_attendance) return [];
                          return Object.entries(workerReport.recent_attendance)
                            .map(([date, list]) => {
                              const d = new Date(date);
                              const name = d.toLocaleDateString("en-US", { weekday: "short" });
                              const present = list.filter(a => a.attendance === "Present").length;
                              const absent = list.filter(a => a.attendance === "Absent").length;
                              return { name, date, Present: present, Absent: absent };
                            })
                            .reverse(); // Dates come in descending order usually
                        })()}
                        barGap={4}
                        onClick={handleAttendanceChartClick}
                      >
                        <CartesianGrid
                          vertical={false}
                          stroke="#E2E8F0"
                          strokeDasharray="3 3"
                        />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#64748B", fontSize: 12 }}
                          dy={10}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: "#64748B", fontSize: 12 }}
                          width={40}
                        />
                        <Tooltip
                          cursor={{ fill: "#F1F5F9" }}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "none",
                            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                          }}
                        />
                        <Bar
                          dataKey="Present"
                          fill="#06B6D4"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                          name="Present"
                        />
                        <Bar
                          dataKey="Absent"
                          fill="#6366F1"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={40}
                          name="Absent"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    {attendancePopup && (
                      <div
                        className="absolute z-10"
                        style={{
                          left: attendancePopup.left,
                          top: attendancePopup.top,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MiniKpiCard
                          title={attendancePopup.label || "Attendance"}
                          value={`${attendancePopup.present || 0} Present`}
                          data={attendancePopup.data}
                          color="#06B6D4"
                        />
                        <div className="mt-1 text-[11px] font-medium text-slate-500 text-center bg-white rounded-md shadow-sm border border-slate-200 py-1">
                          Absent: {attendancePopup.absent || 0}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent Attendance Log */}
                  <div className="w-full xl:w-1/2">
                    <h3 className="text-xs xl:text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
                      Recent Activity
                    </h3>
                    <div className="space-y-3">
                      {(() => {
                        const recentRecords = Object.values(workerReport?.recent_attendance || {}).flat();
                        return recentRecords.slice(0, 5).map((item) => (
                          <div
                            key={item?.id}
                            className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100 group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                <MdOutlinePerson size={18} />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-slate-700">
                                  {item?.worker_name}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {item?.worker_workname || "Worker"} • {item?.attendance_date}
                                </span>
                              </div>
                            </div>

                            <div
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${item?.attendance === "Present"
                                ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                : "bg-rose-50 text-rose-600 border-rose-100"
                                }`}
                            >
                              {item?.attendance}
                            </div>
                          </div>
                        ));
                      })()}
                      {(!workerReport?.recent_attendance || Object.keys(workerReport.recent_attendance).length === 0) && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No recent attendance logs
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          {/* {(userPermissions?.member_control || userPermissions?.is_admin) &&
          gpsEnabled ? (
          <LiveLocationTracker />
        ) : null} */}
        </div>

        {/* Site Communications Panel */}
        {/* <div
        className={`flex flex-col h-full items-center transition-all duration-300 ease-in-out ml-1 ${
          isCommsExpanded ? "w-[30%]" : "w-[4%]"
        }`}
      >
        <CommunicationsPanel
          setTab={setTab}
          setSnagId={setSnagId}
          isExpanded={isCommsExpanded}
          setIsExpanded={setIsCommsExpanded}
        />
      </div> */}
        <div
          className={`flex flex-col h-[91vh] items-center transition-all duration-300 ease-in-out ml-1 ${isCommsExpanded ? "w-[30%]" : "w-[4%]"
            }`}
        >
          <CommunicationsPanel
            setTab={setTab}
            setSnagId={setSnagId}
            isExpanded={isCommsExpanded}
            setIsExpanded={setIsCommsExpanded}
            hideArrow={isProfileCardOpen}
          />
        </div>
      </div>
    </LoadingScreen>
  );
};

export default Home;
